(() => {
  const widget = document.querySelector('[data-contact-widget]');
  if (!widget) return;
  const launcher = widget.querySelector('.hp-contact-widget__launcher');
  const options = widget.querySelector('.hp-contact-widget__options');
  const links = [...options.querySelectorAll('a')];
  const introSessionKey = 'hp-contact-intro-seen-v1';
  const desktopHover = window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');
  let open = false;
  let introConsumed = false;
  let hintStartTimer;
  let hintEndTimer;
  let closingTimer;
  let keyboardFocusPending = false;
  let restoringFocus = false;
  try { introConsumed = sessionStorage.getItem(introSessionKey) === 'true'; } catch { /* Storage can be unavailable in private previews. */ }
  widget.dataset.hint = 'false';

  function consumeIntro() {
    clearTimeout(hintStartTimer);
    if (introConsumed) return;
    introConsumed = true;
    try { sessionStorage.setItem(introSessionKey, 'true'); } catch { /* Keep this page quiet even without storage. */ }
  }

  function dismissHint() {
    consumeIntro();
    clearTimeout(hintEndTimer);
    widget.dataset.hint = 'false';
    delete widget.dataset.hintSource;
  }

  function showHint(source) {
    if (open || widget.dataset.closing === 'true' || widget.dataset.suppressed === 'true' || document.hidden) return;
    consumeIntro();
    clearTimeout(hintEndTimer);
    widget.dataset.hintSource = source;
    widget.dataset.hint = 'true';
    if (source === 'auto') hintEndTimer = setTimeout(dismissHint, 8000);
  }

  function syncOptionsOverflow() {
    const style = getComputedStyle(options);
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const gaps = (parseFloat(style.rowGap) || 0) * Math.max(0, links.length - 1);
    // Transformed entrance positions inflate scrollHeight; only the settled
    // layout determines whether this menu actually needs a scrollport.
    const contentHeight = padding + gaps + links.reduce((height, link) => height + link.offsetHeight, 0);
    const scrollable = String(contentHeight > options.clientHeight + 2);
    if (widget.dataset.scrollable === scrollable) return false;
    widget.dataset.scrollable = scrollable;
    return true;
  }

  function setOpen(next, returnFocus = false) {
    const wasOpen = open;
    open = next && widget.dataset.suppressed !== 'true';
    if (open) {
      const modeChanged = syncOptionsOverflow();
      // Commit a newly constrained closed pose before enabling its entrance.
      if (modeChanged && !wasOpen && links[0]) void getComputedStyle(links[0]).transform;
      dismissHint();
    }
    if (!open && returnFocus) {
      restoringFocus = true;
      try { launcher.focus({ preventScroll: true }); } finally { restoringFocus = false; }
    }
    if (open) {
      clearTimeout(closingTimer);
      closingTimer = undefined;
      delete widget.dataset.closing;
    } else if (wasOpen) {
      widget.dataset.closing = 'true';
      closingTimer = setTimeout(() => {
        delete widget.dataset.closing;
        closingTimer = undefined;
      }, 340);
    }
    widget.dataset.open = String(open);
    launcher.setAttribute('aria-expanded', String(open));
    launcher.setAttribute('aria-label', open ? 'Close contact options' : 'Get in touch');
    options.inert = !open;
    options.setAttribute('aria-hidden', String(!open));
  }

  launcher.addEventListener('click', () => setOpen(!open));
  launcher.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse' && desktopHover.matches) showHint('hover');
  });
  launcher.addEventListener('pointerleave', () => {
    if (widget.dataset.hintSource === 'hover') dismissHint();
  });
  launcher.addEventListener('blur', () => {
    if (widget.dataset.hintSource === 'focus') dismissHint();
  });
  launcher.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
    (event.key === 'ArrowUp' ? links.at(-1) : links[0]).focus();
  });
  widget.addEventListener('keydown', (event) => {
    if (!open || !links.includes(document.activeElement)) return;
    const index = links.indexOf(document.activeElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = index + (event.key === 'ArrowDown' ? 1 : -1);
      (links[next] || launcher).focus();
    }
  });
  document.addEventListener('keydown', (event) => {
    keyboardFocusPending = event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey;
    if (event.key !== 'Escape') return;
    dismissHint();
    if (open) {
      event.preventDefault();
      setOpen(false, true);
    }
  }, true);
  document.addEventListener('pointerdown', (event) => {
    keyboardFocusPending = false;
    dismissHint();
    if (open && !widget.contains(event.target)) {
      setOpen(false, options.contains(document.activeElement));
    }
  }, true);
  document.addEventListener('focusin', (event) => {
    const fromKeyboard = keyboardFocusPending;
    keyboardFocusPending = false;
    if (open && !widget.contains(event.target)) setOpen(false);
    if (event.target === launcher && fromKeyboard && !restoringFocus) showHint('focus');
  }, true);
  window.addEventListener('scroll', dismissHint, { capture: true, passive: true });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) dismissHint();
  });
  desktopHover.addEventListener('change', () => {
    if (!desktopHover.matches && widget.dataset.hintSource === 'hover') dismissHint();
  });
  links.forEach((link) => link.addEventListener('click', () => setOpen(false, true)));

  // Stay out of storefront and builder overlays, including the account sheet's
  // public state attribute (its actual dialog lives inside a shadow root).
  const overlaySelector = [
    '.hp-search-drawer.is-open', '.cart-drawer-overlay.is-open', '.mobile-menu-overlay.is-open', 'dialog[open]',
    '.hp-product-modal.is-open', '.hp-buying-guide-modal.is-open', '.hp-product-help-drawer.is-open',
    '.hp-tier-info__popover.is-open', '.hp-product-guide__fit-info-panel.is-open',
    '.hp-shopify-account[data-hp-account-open]', '#join-service-state:not([hidden])',
    '.bc-drawer.is-opening', '.bc-drawer.is-open', '.bc-image-modal.is-open', '.bc-summary.is-expanded',
    'body.hp-collection-modal-open', 'body.bc-builder-menu-lock',
  ].join(', ');
  let observedBar;
  const barResizeObserver = new ResizeObserver(scheduleSync);
  barResizeObserver.observe(options);
  function syncSurroundings() {
    const suppressed = Boolean(document.querySelector(overlaySelector)) || document.body.classList.contains('is-scroll-locked');
    if (widget.dataset.suppressed !== String(suppressed)) {
      if (suppressed) {
        dismissHint();
        if (widget.contains(document.activeElement)) document.activeElement.blur();
        setOpen(false);
      }
      widget.dataset.suppressed = String(suppressed);
      widget.inert = suppressed;
    }
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    if (stickyBar !== observedBar) {
      if (observedBar) barResizeObserver.unobserve(observedBar);
      observedBar = stickyBar;
      if (observedBar) barResizeObserver.observe(observedBar);
    }
    const bar = stickyBar?.dataset.stuck === 'true' ? stickyBar : null;
    const rect = bar?.getBoundingClientRect();
    const style = bar ? getComputedStyle(bar) : null;
    const ownRect = widget.getBoundingClientRect();
    const overlaps = rect && rect.width > 0 && rect.height > 0
      && style.display !== 'none' && style.visibility !== 'hidden'
      && rect.right > ownRect.left - 12 && rect.left < ownRect.right + 12;
    // Measure its settled footprint, not its in-flight entrance transform.
    const lift = overlaps ? `${rect.height + (parseFloat(style.bottom) || 0) + 16}px` : '0px';
    if (widget.style.getPropertyValue('--contact-lift') !== lift) widget.style.setProperty('--contact-lift', lift);
    syncOptionsOverflow();
  }
  let pending = false;
  function scheduleSync() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => { pending = false; syncSurroundings(); });
  }
  const observer = new MutationObserver((records) => {
    if (records.some((record) => !widget.contains(record.target))) scheduleSync();
  });
  observer.observe(document.body, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['class', 'open', 'data-stuck', 'aria-hidden', 'hidden', 'data-hp-account-open'],
  });
  window.addEventListener('resize', scheduleSync, { passive: true });
  syncSurroundings();
  if (document.hidden) dismissHint();
  if (!introConsumed) {
    hintStartTimer = setTimeout(() => {
      if (!introConsumed) showHint('auto');
    }, 4000);
  }
})();
